import { getAuthPort } from '@/data/auth';
import { config } from '@/data/config';
import { CompilacoesHttpError } from '@/data/http';
import type {
  Acesso,
  Audiencia,
  CompilacoesDataSource,
  EventoVisibilidade,
  Funcionalidade,
  Visibilidade,
  VisibilidadeFuncionalidade,
  VisibilidadeRamo,
} from '@/data/types';
import { AUDIENCIAS, FUNCIONALIDADES } from '@/data/types';
import { ehStaff, MENSAGEM_INDISPONIVEL } from '@/lib/acesso';
import { CAPABILITY_ADMIN, temCapability } from '@/lib/capabilities';
import type { EstadoDemo } from './fixtures';

/**
 * Visibilidade em memória (modo demo) — mesmas regras da API:
 *
 *  - seed: feed `publico`; ramos, incidentes e painel `oculto`;
 *  - `publico` = qualquer autenticado; `colaboradores` = só @nasajon.com.br;
 *    `oculto` = ninguém, exceto STAFF (admin/maintainer/painel/sysadmin), que
 *    nunca tem funcionalidade escondida (`acesso` sempre true para staff);
 *  - rota de funcionalidade sem acesso: 403 `{"message": ...}`;
 *  - PUT igual ao atual = 409; audiência inválida = 422; ramo inexistente = 404;
 *    `publico` num ramo apaga o override;
 *  - só `admin` (ou `sysadmin`) administra (403).
 */

const USUARIO_DEMO = 'demo@nasajon.com.br';
const SEED: Record<Funcionalidade, Audiencia> = { feed: 'publico', ramos: 'oculto', incidentes: 'oculto', painel: 'oculto' };

interface Registro {
  audiencia: Audiencia;
  por: string | null;
  em: string | null;
}

interface EstadoVisibilidade {
  funcionalidades: Map<Funcionalidade, Registro>;
  ramos: Map<string, Registro>;
  eventos: EventoVisibilidade[];
  proximoId: number;
}

let estado: EstadoVisibilidade | null = null;
let colaboradorDemo: boolean | null = null;

function obter(): EstadoVisibilidade {
  estado ??= {
    funcionalidades: new Map(FUNCIONALIDADES.map((f) => [f, { audiencia: SEED[f], por: null, em: null }])),
    ramos: new Map(),
    eventos: [],
    proximoId: 1,
  };
  return estado;
}

/** Só para testes: volta ao seed. */
export function resetDemoVisibilidade(): void {
  estado = null;
  colaboradorDemo = null;
}

/** Só para testes: define a audiência sem passar pela API de admin. */
export function definirAudienciaDemo(funcionalidade: Funcionalidade, audiencia: Audiencia): void {
  obter().funcionalidades.set(funcionalidade, { audiencia, por: null, em: null });
}

/** Só para testes: simula colaborador (`true`) ou cliente externo (`false`). */
export function definirColaboradorDemo(valor: boolean | null): void {
  colaboradorDemo = valor;
}

function ehColaborador(): boolean {
  return colaboradorDemo ?? config.demoColaborador;
}

function enxerga(audiencia: Audiencia, colaborador: boolean): boolean {
  return audiencia === 'publico' || (audiencia === 'colaboradores' && colaborador);
}

/** `acesso` do `/me`: staff tem tudo (mesmo `oculto`); os demais, conforme a audiência. */
export function acessoDemo(roles: string[]): Acesso {
  const colaborador = ehColaborador();
  const staff = colaborador && ehStaff(roles); // capability só vale para colaborador (como na API)
  const e = obter();
  return Object.fromEntries(
    FUNCIONALIDADES.map((f) => [f, staff || enxerga(e.funcionalidades.get(f)!.audiencia, colaborador)]),
  ) as Acesso;
}

export function colaboradorDoDemo(): boolean {
  return ehColaborador();
}

function erro(recurso: string, status: number, detail: string): CompilacoesHttpError {
  return new CompilacoesHttpError(recurso, status, JSON.stringify({ detail }));
}

/** 403 igual ao da API quando o viewer não enxerga a funcionalidade e não é staff. */
export async function exigirAcesso(funcionalidade: Funcionalidade, recurso: string): Promise<void> {
  const roles = await getAuthPort().getRoles();
  if (acessoDemo(roles)[funcionalidade]) return;
  throw new CompilacoesHttpError(recurso, 403, JSON.stringify({ message: MENSAGEM_INDISPONIVEL }));
}

/** Predicado de ramo visível para o viewer atual (staff vê todos). */
export async function visibilidadeDeRamos(): Promise<(produto: string, ramo: string) => boolean> {
  const roles = await getAuthPort().getRoles();
  if (ehStaff(roles)) return () => true;
  const colaborador = ehColaborador();
  return (produto, ramo) => enxerga(obter().ramos.get(`${produto}/${ramo}`)?.audiencia ?? 'publico', colaborador);
}

/** Embrulha métodos de uma funcionalidade para devolver 403 sem acesso. */
export function proteger<T extends object>(metodos: T, funcionalidade: Funcionalidade, recurso: string): T {
  const copia: Record<string, unknown> = {};
  for (const [nome, fn] of Object.entries(metodos)) {
    copia[nome] = async (...args: unknown[]) => {
      await exigirAcesso(funcionalidade, recurso);
      return (fn as (...a: unknown[]) => unknown)(...args);
    };
  }
  return copia as T;
}

export interface ContextoVisibilidade {
  estado: () => EstadoDemo;
  simular: <T>(signal: AbortSignal | undefined, gerar: () => T) => Promise<T>;
}

type Metodos = Pick<
  CompilacoesDataSource,
  | 'listarVisibilidade'
  | 'alterarVisibilidadeFuncionalidade'
  | 'alterarVisibilidadeRamo'
  | 'listarEventosVisibilidade'
>;

export function criarVisibilidadeDemo({ estado: estadoDemo, simular }: ContextoVisibilidade): Metodos {
  function exigirAdmin(recurso: string, roles: string[]): void {
    if (!temCapability(roles, CAPABILITY_ADMIN)) {
      throw erro(recurso, 403, 'Administrar a visibilidade exige a capability "admin".');
    }
  }

  function registrar(alvo: string, anterior: Audiencia, nova: Audiencia): void {
    const e = obter();
    e.eventos.unshift({
      id: e.proximoId++,
      alvo,
      audiencia_anterior: anterior,
      audiencia_nova: nova,
      usuario: USUARIO_DEMO,
      criado_em: new Date().toISOString(),
    });
  }

  function validar(recurso: string, audiencia: unknown): Audiencia {
    if (!AUDIENCIAS.includes(audiencia as Audiencia)) {
      throw erro(recurso, 422, `Audiência inválida: ${String(audiencia)}. Use publico, colaboradores ou oculto.`);
    }
    return audiencia as Audiencia;
  }

  function linhaRamo(produto: string, ramo: string, total: number): VisibilidadeRamo {
    const r = obter().ramos.get(`${produto}/${ramo}`);
    return {
      produto,
      ramo,
      audiencia: r?.audiencia ?? 'publico',
      total_builds: total,
      atualizado_por: r?.por ?? null,
      atualizado_em: r?.em ?? null,
    };
  }

  function totalDoRamo(produto: string, ramo: string): number {
    return estadoDemo().builds.filter((b) => b.produto === produto && b.ramo === ramo).length;
  }

  return {
    async listarVisibilidade(signal) {
      const roles = await getAuthPort().getRoles();
      return simular(signal, (): Visibilidade => {
        exigirAdmin('a visibilidade', roles);
        const e = obter();
        const pares = new Map<string, { produto: string; ramo: string; total: number }>();
        for (const b of estadoDemo().builds) {
          const chave = `${b.produto}/${b.ramo}`;
          const atual = pares.get(chave);
          if (atual) atual.total += 1;
          else pares.set(chave, { produto: b.produto, ramo: b.ramo, total: 1 });
        }
        return {
          funcionalidades: FUNCIONALIDADES.map((f): VisibilidadeFuncionalidade => {
            const r = e.funcionalidades.get(f)!;
            return { funcionalidade: f, audiencia: r.audiencia, atualizado_por: r.por, atualizado_em: r.em };
          }),
          ramos: [...pares.values()]
            .sort((a, b) => a.produto.localeCompare(b.produto) || b.ramo.localeCompare(a.ramo))
            .map((p) => linhaRamo(p.produto, p.ramo, p.total)),
          audiencias: [...AUDIENCIAS],
        };
      });
    },

    async alterarVisibilidadeFuncionalidade(funcionalidade, audiencia) {
      const roles = await getAuthPort().getRoles();
      return simular(undefined, (): VisibilidadeFuncionalidade => {
        const recurso = 'a visibilidade da funcionalidade';
        exigirAdmin(recurso, roles);
        if (!FUNCIONALIDADES.includes(funcionalidade)) throw erro(recurso, 404, `Funcionalidade ${funcionalidade} não existe.`);
        const nova = validar(recurso, audiencia);
        const e = obter();
        const atual = e.funcionalidades.get(funcionalidade)!;
        if (atual.audiencia === nova) throw erro(recurso, 409, 'A funcionalidade já está com esta audiência.');
        registrar(`funcionalidade:${funcionalidade}`, atual.audiencia, nova);
        const registro = { audiencia: nova, por: USUARIO_DEMO, em: new Date().toISOString() };
        e.funcionalidades.set(funcionalidade, registro);
        return { funcionalidade, audiencia: nova, atualizado_por: registro.por, atualizado_em: registro.em };
      });
    },

    async alterarVisibilidadeRamo(produto, ramo, audiencia) {
      const roles = await getAuthPort().getRoles();
      return simular(undefined, (): VisibilidadeRamo => {
        const recurso = 'a visibilidade do ramo';
        exigirAdmin(recurso, roles);
        const total = totalDoRamo(produto, ramo);
        if (total === 0) throw erro(recurso, 404, `Ramo ${ramo} não encontrado no produto ${produto}.`);
        const nova = validar(recurso, audiencia);
        const e = obter();
        const chave = `${produto}/${ramo}`;
        const anterior = e.ramos.get(chave)?.audiencia ?? 'publico';
        if (anterior === nova) throw erro(recurso, 409, 'O ramo já está com esta audiência.');
        registrar(`ramo:${produto}:${ramo}`, anterior, nova);
        if (nova === 'publico') e.ramos.delete(chave);
        else e.ramos.set(chave, { audiencia: nova, por: USUARIO_DEMO, em: new Date().toISOString() });
        return linhaRamo(produto, ramo, total);
      });
    },

    async listarEventosVisibilidade(limit = 50, signal) {
      const roles = await getAuthPort().getRoles();
      return simular(signal, () => {
        exigirAdmin('o histórico de visibilidade', roles);
        return obter().eventos.slice(0, limit);
      });
    },
  };
}
